import styled from '@emotion/styled';
import { AppLink } from '../types/app-link';
import { Capacitor } from '@capacitor/core';
import { toast } from 'react-toastify';

export const toPascalCase = (str: string): string => {
  return str.replace(/\w+/g, (word: string) => {
    return word[0].toUpperCase() + word.slice(1).toLowerCase();
  });
};

export const getOrdinal = (n: number): JSX.Element => {
  if (n === 0) return <>--</>;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  const suffix = s[(v - 20) % 10] || s[v] || s[0];
  return (
    <OrdinalSuffixContainer>
      {n}
      <SuffixContainer>{suffix}</SuffixContainer>
    </OrdinalSuffixContainer>
  );
};

const OrdinalSuffixContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SuffixContainer = styled.div`
  font-size: 0.75em;
  position: relative;
  // we want the suffix to be a little above the number
  top: -0.2em;
`;

export const validatePhone = (value: string): boolean => {
  const validPhoneNumberRegex = /^(03|3)[0-9]{9}$/;
  return validPhoneNumberRegex.test(value);
};

export const isValueEmpty = (value: string): boolean => {
  return value.trim() === '';
};

export const nonInitials = (value: string): boolean => {
  const numberStart = /^[03]/;
  return value !== '' ? numberStart.test(value) : true;
};

export const validatePhoneNumber = (value: string) => {
  // Check if it's an international number (starts with +)
  if (value.startsWith('+')) {
    // Remove + and any spaces
    const cleanNumber = value.replace(/[\s+]/g, '');
    // Basic international validation: should be 7-15 digits
    if (cleanNumber.length < 7) {
      return 'Phone number too short.';
    } else if (cleanNumber.length > 15) {
      return 'Phone number too long.';
    } else if (!/^\d+$/.test(cleanNumber)) {
      return 'Please enter a valid phone number.';
    }
    return '';
  }

  // Existing Pakistani phone validation
  if (!nonInitials(value)) {
    return 'Phone number must start with 3 or 0.';
  } else if (isValueEmpty(value)) {
    return 'Please enter your phone number.';
  } else if (!validatePhone(value)) {
    return 'Please enter a valid phone number.';
  } else {
    return '';
  }
};

export const validateNameInput = (value: string) => {
  return !/^[a-zA-Z\s.-]*$/.test(value);
};

export const copyLinkToClipboard = async () => {
  try {
    let url: AppLink;
    switch (Capacitor.getPlatform()) {
      case 'ios':
        url = AppLink.Ios;
        break;
      case 'android':
        url = AppLink.Android;
        break;
      default:
        url = AppLink.Default;
    }
    await navigator.clipboard.writeText(url);
    toast('Link copy to clipboard');
  } catch (err) {
    toast('Failed to copy link');
  }
};

export const maskPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber.length <= 4) {
    return phoneNumber;
  }
  const maskedSection = '*'.repeat(phoneNumber.length - 4);
  const visibleSection = phoneNumber.slice(-4);
  return `${maskedSection}${visibleSection}`;
};

export const decodeUrl = (
  url: string,
): { username: string | null; name: string | null } => {
  try {
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.slice(1).split('/');
    const username = pathSegments[0].split('=')[1];
    const name = decodeURIComponent(pathSegments[1].split('=')[1]);
    return { username, name };
  } catch (error) {
    console.error('Invalid URL:', error);
    return { username: null, name: null };
  }
};
